// Day-first dates for families in Australia.
// The browser's own date box follows the computer's language setting, so a US-English browser reads
// 05/10/2015 as 10 May. These helpers never guess: the day, month and year are entered separately
// (or typed as DD/MM/YYYY), checked, and read back in words. Stored values are unchanged:
// YYYY-MM-DD for date fields, DD/MM/YYYY for the questionnaire's Assessment date.

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const MIN_YEAR = 1900;

const pad = (n) => String(n).padStart(2, "0");

export function todayIso(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// A real calendar date? (31 April and 29 February 2027 are not.)
export function isRealDate(y, m, d) {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function isoFromParts(day, month, year) {
  const d = Number(day), m = Number(month), y = Number(year);
  if (!/^\d{1,2}$/.test(String(day).trim()) || !/^\d{4}$/.test(String(year).trim())) return null;
  if (!isRealDate(y, m, d)) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function partsFromIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || "").trim());
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  if (!isRealDate(y, mo, d)) return null;
  return { day: String(d), month: String(mo), year: m[1] };
}

export function inWords(iso) {
  const p = partsFromIso(iso);
  if (!p) return "";
  return `${Number(p.day)} ${MONTHS[Number(p.month) - 1]} ${p.year}`;
}

// "5/10/2015", "05-10-2015", "5.10.2015" -> "2015-10-05". Day first, always. Four-digit years only.
export function parseDayFirst(text) {
  const m = /^\s*(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{4})\s*$/.exec(String(text || ""));
  if (!m) return null;
  return isoFromParts(m[1], m[2], m[3]);
}

export function toDayFirst(iso) {
  const p = partsFromIso(iso);
  return p ? `${pad(p.day)}/${pad(p.month)}/${p.year}` : "";
}

// What is wrong with what has been typed so far? Returns "" when the entry is fine (or empty).
// parts: { day, month, year } as typed. opts: { notFuture, today }
export function problemWith(parts, opts = {}) {
  const day = String(parts.day || "").trim();
  const month = String(parts.month || "").trim();
  const year = String(parts.year || "").trim();
  if (!day && !month && !year) return "";
  if (!day || !month || !year) return "Please add the day, month and year.";
  if (!/^\d{1,2}$/.test(day)) return "The day should be a number, like 5.";
  if (!/^\d{4}$/.test(year)) return "Please write the year in full, like 2015.";
  const iso = isoFromParts(day, month, year);
  if (!MONTHS[Number(month) - 1]) return "Please choose the month.";
  if (!iso) return `There is no ${Number(day)} ${MONTHS[Number(month) - 1]} ${year}. Please check the date.`;
  if (Number(year) < MIN_YEAR) return "Please check the year.";
  const today = opts.today || todayIso();
  if (opts.notFuture && iso > today) return "This date can't be in the future.";
  if (opts.maxYearsAhead != null && Number(year) > Number(today.slice(0, 4)) + opts.maxYearsAhead) {
    return "Please check the year.";
  }
  return "";
}

// Field ids and question text that mean "date of birth": never in the future.
export function isBirthDate(idOrText) {
  return /birth|\bdob\b|_dob$|^dob/i.test(String(idOrText || ""));
}

// The questionnaire "Assessment date" (typed DD/MM/YYYY; it goes into the PDF file name).
// It stays editable because clinicians may backdate observation forms, but it must be a real date
// and not in the future.
export function assessmentDateProblem(text, today = todayIso()) {
  const s = String(text || "").trim();
  if (!s) return "Please add the assessment date (day/month/year).";
  const iso = parseDayFirst(s);
  if (!iso) {
    if (/^\s*\d{1,2}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{2}\s*$/.test(s)) return "Please write the year in full, like 20/10/2026.";
    if (/^\s*\d{1,2}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{4}\s*$/.test(s)) return "That date doesn't exist. Please check the day and month.";
    return "Please write the date as day/month/year, like 20/10/2026.";
  }
  if (iso > today) return "The assessment date can't be in the future.";
  if (Number(iso.slice(0, 4)) < MIN_YEAR) return "Please check the year.";
  return "";
}
